import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Sun, CloudRain, Snowflake, Quote, Github, Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WEATHER_CONDITIONS, PROGRAMMING_QUOTES } from '@/data/widgets-data';

// Clock Widget
export function ClockWidget() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  // Calculate rotation angles
  const hourRotation = (hours % 12) * 30 + minutes * 0.5;
  const minuteRotation = minutes * 6;
  const secondRotation = seconds * 6;

  return (
    <motion.div
      className="bg-card/80 backdrop-blur-xl rounded-2xl p-6 border border-border shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="flex items-center gap-2 mb-6">
        <Clock className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Clock</span>
      </div>

      {/* Analog clock */}
      <div className="relative w-36 h-36 mx-auto mb-6">
        {/* Clock face */}
        <div className="absolute inset-0 rounded-full border-4 border-border bg-background shadow-inner overflow-hidden">
          {/* Hour markers */}
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180); // Convert to radians, offset by -90 for 12 o'clock
            const radius = 58; // Distance from center
            const isMain = i % 3 === 0;
            const x = 72 + Math.cos(angle) * radius; // 72 = half of 144px (w-36)
            const y = 72 + Math.sin(angle) * radius;
            return (
              <div
                key={i}
                className="absolute"
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                  transform: `translate(-50%, -50%) rotate(${i * 30}deg)`,
                }}
              >
                <div
                  className={cn(
                    "rounded-full",
                    isMain ? "w-1.5 h-3 bg-foreground/60" : "w-1 h-1.5 bg-foreground/30"
                  )}
                />
              </div>
            );
          })}

          {/* Clock hands container */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Hour hand */}
            <div
              className="absolute w-1.5 bg-foreground rounded-full shadow-sm"
              style={{
                height: '28%',
                bottom: '50%',
                transformOrigin: 'center bottom',
                transform: `rotate(${hourRotation}deg)`,
              }}
            />

            {/* Minute hand */}
            <div
              className="absolute w-1 bg-primary rounded-full shadow-sm"
              style={{
                height: '38%',
                bottom: '50%',
                transformOrigin: 'center bottom',
                transform: `rotate(${minuteRotation}deg)`,
              }}
            />

            {/* Second hand */}
            <div
              className="absolute w-0.5 bg-red-500 rounded-full"
              style={{
                height: '42%',
                bottom: '50%',
                transformOrigin: 'center bottom',
                transform: `rotate(${secondRotation}deg)`,
                transition: 'transform 0.1s linear',
              }}
            />

            {/* Center dot */}
            <div className="absolute w-3 h-3 bg-primary rounded-full shadow-md border-2 border-background z-10" />
          </div>
        </div>
      </div>

      {/* Digital time */}
      <div className="text-center space-y-1">
        <p className="text-3xl font-mono font-bold text-foreground tabular-nums">
          {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </p>
        <p className="text-xs text-muted-foreground">
          {time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </p>
      </div>
    </motion.div>
  );
}

// Weather Widget
export function WeatherWidget() {
  const [weather] = useState(() => WEATHER_CONDITIONS[Math.floor(Math.random() * WEATHER_CONDITIONS.length)]);

  const WeatherIcon = weather.icon;

  return (
    <motion.div
      className="bg-card/80 backdrop-blur-xl rounded-2xl p-6 border border-border shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Weather</span>
        <span className="text-xs text-muted-foreground">Jakarta, ID</span>
      </div>

      <div className="flex items-center gap-4">
        <motion.div
          animate={{
            y: [0, -5, 0],
            rotate: weather.label === 'Sunny' ? [0, 10, 0] : 0,
          }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <WeatherIcon className={cn('w-12 h-12', weather.color)} />
        </motion.div>

        <div>
          <p className="text-4xl font-light text-foreground">
            {weather.temp}°
          </p>
          <p className="text-sm text-muted-foreground">{weather.label}</p>
        </div>
      </div>

      {/* Forecast */}
      <div className="mt-4 pt-4 border-t border-border flex justify-between">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => {
          const forecast = WEATHER_CONDITIONS[(i + 1) % WEATHER_CONDITIONS.length];
          const ForecastIcon = forecast.icon;
          return (
            <div key={day} className="text-center">
              <p className="text-xs text-muted-foreground mb-1">{day}</p>
              <ForecastIcon className={cn('w-4 h-4 mx-auto', forecast.color)} />
              <p className="text-xs text-foreground mt-1">{forecast.temp}°</p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// GitHub Contribution Graph Widget
export function GitHubWidget() {
  // Generate mock contribution data
  const generateContributions = () => {
    const contributions = [];
    for (let week = 0; week < 20; week++) {
      for (let day = 0; day < 7; day++) {
        contributions.push(Math.floor(Math.random() * 5));
      }
    }
    return contributions;
  };

  const [contributions] = useState(generateContributions);

  const getColor = (level: number) => {
    switch (level) {
      case 0: return 'bg-secondary';
      case 1: return 'bg-green-900/50';
      case 2: return 'bg-green-700/70';
      case 3: return 'bg-green-500/80';
      case 4: return 'bg-green-400';
      default: return 'bg-secondary';
    }
  };

  return (
    <motion.div
      className="bg-card/80 backdrop-blur-xl rounded-2xl p-6 border border-border shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Github className="w-4 h-4 text-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">GitHub Activity</span>
      </div>

      {/* Contribution graph */}
      <div className="flex gap-1 overflow-hidden">
        {[...Array(20)].map((_, weekIndex) => (
          <div key={weekIndex} className="flex flex-col gap-1">
            {[...Array(7)].map((_, dayIndex) => {
              const index = weekIndex * 7 + dayIndex;
              const level = contributions[index] || 0;
              return (
                <motion.div
                  key={dayIndex}
                  className={cn('w-2.5 h-2.5 rounded-sm', getColor(level))}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.002 }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="mt-4 pt-4 border-t border-border flex justify-between text-sm">
        <div>
          <p className="text-2xl font-bold text-foreground">847</p>
          <p className="text-xs text-muted-foreground">contributions</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">12</p>
          <p className="text-xs text-muted-foreground">streak days</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">42</p>
          <p className="text-xs text-muted-foreground">repositories</p>
        </div>
      </div>
    </motion.div>
  );
}

// Quote Widget
export function QuoteWidget() {
  const [quote] = useState(() => PROGRAMMING_QUOTES[Math.floor(Math.random() * PROGRAMMING_QUOTES.length)]);

  return (
    <motion.div
      className="bg-card/80 backdrop-blur-xl rounded-2xl p-6 border border-border shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Quote className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quote of the Day</span>
      </div>

      <blockquote className="relative">
        <span className="text-4xl text-primary/20 absolute -top-2 -left-2">"</span>
        <p className="text-sm text-foreground leading-relaxed pl-4">
          {quote.text}
        </p>
        <footer className="mt-3 text-xs text-muted-foreground pl-4">
          — {quote.author}
        </footer>
      </blockquote>
    </motion.div>
  );
}

// Calendar Mini Widget
export function CalendarWidget() {
  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).getDay();

  return (
    <motion.div
      className="bg-card/80 backdrop-blur-xl rounded-2xl p-6 border border-border shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-4 h-4 text-primary" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <span key={i} className="text-[10px] text-muted-foreground font-medium">
            {day}
          </span>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {/* Empty cells for days before month starts */}
        {[...Array(firstDayOfMonth)].map((_, i) => (
          <span key={`empty-${i}`} />
        ))}

        {/* Day cells */}
        {[...Array(daysInMonth)].map((_, i) => {
          const dayNum = i + 1;
          const isToday = dayNum === today.getDate();
          return (
            <motion.span
              key={dayNum}
              className={cn(
                'text-xs w-6 h-6 flex items-center justify-center rounded-full',
                isToday
                  ? 'bg-primary text-primary-foreground font-bold'
                  : 'text-foreground hover:bg-secondary cursor-pointer'
              )}
              whileHover={{ scale: 1.1 }}
            >
              {dayNum}
            </motion.span>
          );
        })}
      </div>
    </motion.div>
  );
}

// Combined Widget Container
interface DesktopWidgetsProps {
  enabled?: boolean;
}

export function DesktopWidgets({ enabled = false }: DesktopWidgetsProps) {
  if (!enabled) return null;

  return (
    <div className="absolute top-4 right-4 flex flex-col gap-4 z-10 max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-hide">
      <ClockWidget />
      <WeatherWidget />
      <GitHubWidget />
      <QuoteWidget />
    </div>
  );
}
